import React, { useEffect, useMemo, useState } from 'react';
import { Facet } from '#types/generated/graphql-types';
import MenuItem from '#components/menu/MenuItem';
import Collapse from '#components/transition/collapse/Collapse';
import s from '#modules/facets/facet.module.scss';
import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';

type Props = {
  loading: boolean;
  facet: Facet;
  selectedValues: Set<string>;
  onChange: (facetName: string, newSelection: Set<string>) => void;
};

type FacetOption = {
  id: string;
  label: string;
  count: number;
};

const CountFacet = ({ facet, selectedValues, loading, onChange }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  // Auto-open if items are selected
  useEffect(() => {
    if (selectedValues && selectedValues.size > 0) {
      setIsOpen(true);
    }
  }, [selectedValues]);

  const options = useMemo<FacetOption[] | null>(() => {
    if (facet.__typename !== 'CountFacet' || !facet.values) {
      return null;
    }

    return facet.values.map((item) => ({
      id: item.slug || String(item.value),
      label: item.label || String(item.value),
      count: item.count || 0,
    }));
  }, [facet]);

  const handleChange = (slug: string) => {
    const newSelection = new Set(selectedValues);
    if (newSelection.has(slug)) {
      newSelection.delete(slug);
    } else {
      newSelection.add(slug);
    }
    onChange(facet.name, newSelection);
  };

  const linkClass = (slug: string) =>
    classNames(s.link, {
      [s.isActive]: selectedValues?.has(slug),
    });

  if (facet.__typename !== 'CountFacet' || !facet.values) {
    return null;
  }

  if (loading || !options || options.length === 0) {
    return null;
  }

  const label =
    facet.name === 'categorySlugs'
      ? t('filterCategory')
      : facet.name === 'years'
        ? t('filterDate')
        : facet.label;

  return (
    <MenuItem divider noPadding>
      <button
        type="button"
        className={`${s.accordionButton} ${
          selectedValues?.size > 0 ? s.activeAccordionButton : ''
        }`}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <div className={s.facetCategoryContainer}>
          {label}
          <div
            className={`${s.facetCategoryNumberSelected} ${
              selectedValues.size <= 0 ? s.hiddenNumber : ''
            }`}
          >
            {selectedValues.size}
          </div>
        </div>
      </button>

      <Collapse open={isOpen} id={`submenu-${facet.name}`}>
        <ul className={s.filterHost}>
          {options.map((option) => (
            <li className={s.item} key={option.id}>
              <button
                className={linkClass(option.id)}
                onClick={() => handleChange(option.id)}
                type="button"
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      </Collapse>
    </MenuItem>
  );
};

export default CountFacet;
